import { useMemo, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router';
import { css, useTheme } from '@emotion/react';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import styled from 'types/theme.types';
import { Tag, legacyAntdTagMargin } from 'components/common/tag';
import UseIsMobile from 'hooks/use-is-mobile';
import type { Organization } from 'types/api.types';
import { useGetBillingInformation } from 'stores/rq/billing';
import NavbarHeader from 'components/layout/navbar/navbar-header';
import NavbarMenuList, { type ExtendedMenuItem } from 'components/layout/navbar/navbar-menu-list';
import type { RailGroup } from 'components/layout/navbar/navbar-rail-groups';
import {
  SettingOutlined,
  CloudOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  HomeOutlined,
  SnippetsOutlined,
  StockOutlined,
  BankOutlined,
  CodeOutlined,
  FolderOpenOutlined,
  SafetyOutlined,
  BarChartOutlined,
  AlertOutlined
} from '@ant-design/icons';
import { useHasPermissionInAnyScope } from 'hooks/use-has-permission';
import { usePayGateFeature } from 'hooks/use-pay-gate-feature';

interface Props {
  organization: Organization;
}

const OrganizationMenu = ({ organization }: Props) => {
  const location = useLocation();
  const { isMobile } = UseIsMobile();
  const lastProjectLocation = useRef<string>();
  const { isNewDesign } = useTheme();

  const { isLoading: isLoadingBillingInformation, data: billingData } = useGetBillingInformation();
  const { isAuthorized: canViewDriftCauses, isLoading: isLoadingDriftCausePermission } =
    useHasPermissionInAnyScope('VIEW_DRIFT_CAUSE');
  const { activated: isDriftActionsActivated } = usePayGateFeature('driftActions');

  useEffect(() => {
    if (location.pathname.includes('/p/')) {
      lastProjectLocation.current = location.pathname;
    }
  }, [location.pathname]);

  const menuItems = useMemo<ExtendedMenuItem[]>(() => {
    const insightsActivated = billingData?.insights?.activated;

    const analyticsItem = insightsActivated
      ? {
          key: 'org-menu-analytics',
          to: () => '/cloud-analyst',
          title: <FormattedMessage id="cloud-analyst" />,
          icon: <StockOutlined />,
          isLoading: isLoadingBillingInformation
        }
      : {
          key: 'org-menu-dashboards',
          to: () => '/dashboards',
          title: <FormattedMessage id="dashboards" />,
          icon: <DashboardOutlined />,
          isLoading: isLoadingBillingInformation
        };

    return [
      {
        key: 'org-menu-homepage',
        exact: true,
        to: () => '/',
        title: <FormattedMessage id="homepage" />,
        icon: <HomeOutlined />,
        isLoading: isLoadingBillingInformation
      },
      {
        key: 'org-menu-projects',
        exact: true,
        to: () => '/projects',
        title: <FormattedMessage id="projects" />,
        icon: <FolderOpenOutlined />,
        isLoading: isLoadingBillingInformation
      },
      {
        key: 'org-menu-templates',
        to: () => '/templates',
        title: <FormattedMessage id="navigation.organization.templates" />,
        icon: <SnippetsOutlined />,
        isLoading: isLoadingBillingInformation
      },
      {
        key: 'org-menu-policies',
        to: () => '/policies',
        title: <FormattedMessage id="navigation.policies" />,
        icon: <SafetyOutlined />,
        requiredPermissions: ['VIEW_ORGANIZATION'],
        isLoading: isLoadingBillingInformation
      },
      ...(isMobile
        ? []
        : [
            {
              key: 'org-menu-variables',
              to: () => '/variables',
              title: <FormattedMessage id="navigation.organization.variables" />,
              icon: <CodeOutlined />,
              isLoading: isLoadingBillingInformation
            }
          ]),
      {
        key: 'org-menu-registry',
        to: () => `/registry`,
        title: <FormattedMessage id="registry" />,
        icon: <DatabaseOutlined />,
        requiredPermissions: ['VIEW_MODULES', 'VIEW_PROVIDERS'],
        isLoading: isLoadingBillingInformation
      },
      {
        key: 'cloud',
        to: () => `/cloud`,
        title: <FormattedMessage id="cloud" />,
        icon: <CloudOutlined />,
        requiredPermissions: ['VIEW_DASHBOARD'],
        isLoading: isLoadingBillingInformation
      },
      {
        key: 'org-menu-code-optimizer',
        to: () => '/code-optimizer',
        title: (
          <>
            <FormattedMessage id="code-optimizer" />
            <BetaTag color="rgba(210, 247, 64, 0.16)" newDesignPreset="lime">
              <FormattedMessage id="beta" />
            </BetaTag>
          </>
        ),
        icon: <BarChartOutlined />,
        requiredPermissions: ['VIEW_DASHBOARD'],
        isLoading: isLoadingBillingInformation
      },
      {
        ...analyticsItem,
        requiredPermissions: ['VIEW_DASHBOARD']
      },
      {
        key: 'org-menu-settings',
        to: () => `/organizations/${organization.id}`,
        title: <FormattedMessage id="navigation.organization.settings" />,
        icon: <SettingOutlined />,
        requiredPermissions: [
          'EDIT_ORGANIZATION_SETTINGS',
          'VIEW_VCS_CONNECTIONS',
          'MANAGE_VCS_CONNECTIONS',
          'MANAGE_BILLING',
          'CREATE_CUSTOM_ROLES',
          'VIEW_AUDIT_LOGS'
        ],
        isLoading: isLoadingBillingInformation
      },
      {
        key: 'org-menu-actions-title',
        title: <FormattedMessage id="navigation.actions" />,
        isSectionTitle: true as const,
        requiredPermissions: ['VIEW_ORGANIZATION'],
        shouldShow: canViewDriftCauses && isDriftActionsActivated,
        isLoading: isLoadingBillingInformation || isLoadingDriftCausePermission
      },
      {
        key: 'org-menu-drifts',
        to: () => '/actions',
        title: <FormattedMessage id="navigation.organization.drifts" />,
        icon: <AlertOutlined />,
        requiredPermissions: ['VIEW_ORGANIZATION'],
        shouldShow: canViewDriftCauses && isDriftActionsActivated,
        isLoading: isLoadingBillingInformation || isLoadingDriftCausePermission
      }
    ];
  }, [
    billingData,
    organization.id,
    isMobile,
    isLoadingBillingInformation,
    canViewDriftCauses,
    isDriftActionsActivated,
    isLoadingDriftCausePermission
  ]);

  return (
    <>
      <NavbarHeader menuTitle="Organization Menu" menuIcon={isNewDesign ? undefined : <BankOutlined />} />
      <NavbarMenuList items={menuItems} groups={NEW_DESIGN_GROUPS} />
    </>
  );
};

const NEW_DESIGN_GROUPS: RailGroup[] = [
  { keys: ['org-menu-homepage', 'org-menu-projects'] },
  {
    titleId: 'navigation.section.configuration',
    keys: ['org-menu-templates', 'org-menu-policies', 'org-menu-variables', 'org-menu-registry']
  },
  {
    titleId: 'navigation.section.insights',
    keys: ['cloud', 'org-menu-code-optimizer', 'org-menu-analytics', 'org-menu-dashboards']
  },
  { keys: ['org-menu-actions-title', 'org-menu-drifts'] },
  { titleId: 'navigation.section.settings', keys: ['org-menu-settings'] }
];

// The pre-restyle tag carried its colour on an inline style and antd's own trailing margin.
// The wrapper's ellipsis `overflow: hidden` moves an inline-block's baseline to its bottom margin edge,
// which lifts the tag off the label's centre - the raw antd tag it replaced had no overflow to shift it.
const BetaTag = styled(Tag)`
  margin-left: 8px;
  vertical-align: middle;

  ${legacyAntdTagMargin}

  ${legacyOnly(css`
    && {
      color: rgba(210, 247, 64, 1);
    }
  `)}

  /* Figma's rail badge is a filled lime pill; the lime Tag preset is the pale tint used on page
     content, which disappears against the dark rail. Doubled because antd declares background and
     color on .ant-tag, so a single class ties on specificity. */
  ${newDesignOnly(css`
    && {
      background: var(--surface-primary-action);
      border-color: var(--surface-primary-action);
      color: var(--text-on-action);
    }
  `)}
`;

export default OrganizationMenu;
